import { Button } from "@material-ui/core"
import Edit from "@material-ui/icons/Edit"
import Publish from "@material-ui/icons/Publish"
import Save from "@material-ui/icons/Save"
import Share from "@material-ui/icons/Share"
import NestedMenuItem from "material-ui-nested-menu-item"
import { useMemo } from "react"
import type { ReactElement, ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { ContextMenuButton, IconMenuItem } from ".."
import { useIsOwner, useIsSafeExamBrowser } from "../../../hooks"
import type { IHasId, IHasOwner, IHasTitle } from "../../../redux"
import { mapObjectDescriptorToTranslation } from "../../../utils"
import { useAuthenticatedUser } from "../../indentity-provider.component"

import "./more-options-base.styles.scss"

export type MenuOption = {
  isShown: boolean
  buttonParams: MenuOptionButtonParams
}

export type MenuOptionButtonParams = {
  icon: ReactElement
  text: string
  onClick?: () => void
  nestedItems?: ReactNode
}

export interface IMoreOptionsBaseProps {
  data: IHasId & IHasOwner & IHasTitle
  options: MenuOption[]
  objectDescriptor: "map" | "comparison"
  onSave: () => void
  onSaveAs: () => void
  onHandIn: () => void
  onShare: () => void
}

export function MoreOptionsBase(props: IMoreOptionsBaseProps): ReactElement {
  const { data, options, objectDescriptor, onSave, onSaveAs, onHandIn, onShare } = props

  const { t } = useTranslation("general")
  const isOwner = useIsOwner(data)
  const isSafeExamBrowser = useIsSafeExamBrowser()

  const { isTeacher } = useAuthenticatedUser()

  const showChangeTitleOption: boolean = useMemo<boolean>(() => {
    return isOwner && !!data.title
  }, [isOwner, data])

  const showSaveAs: boolean = useMemo<boolean>(() => {
    return !isSafeExamBrowser && !!data.id
  }, [isSafeExamBrowser, data])

  const showHandInAssignment: boolean = useMemo<boolean>(() => {
    return !isSafeExamBrowser && !isTeacher && !!data.id
  }, [isSafeExamBrowser, isTeacher, data])

  const showShareWithClass: boolean = useMemo<boolean>(() => {
    return isTeacher
  }, [isTeacher])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  const optionsToShow: MenuOption[] = useMemo<MenuOption[]>(() => {
    return [
      ...options,
      {
        isShown: showChangeTitleOption,
        buttonParams: {
          icon: <Edit />,
          text: t("optionsButton.editTitle"),
          onClick: onSave
        }
      },
      {
        isShown: showSaveAs,
        buttonParams: {
          icon: <Save />,
          text: t("optionsButton.saveAs"),
          onClick: onSaveAs
        }
      },
      {
        isShown: showHandInAssignment,
        buttonParams: {
          icon: <Publish />,
          text: t("optionsButton.handInAssignment.handInAssignmentTitle", {
            type: mapObjectDescriptorToTranslation(objectDescriptor)
          }),
          onClick: onHandIn
        }
      },
      {
        isShown: showShareWithClass,
        buttonParams: {
          icon: <Share />,
          text: t("optionsButton.shareAssignment", {
            assignment: mapObjectDescriptorToTranslation(objectDescriptor)
          }),
          onClick: onShare
        }
      }
    ].filter((option: MenuOption) => option.isShown)
  }, [showChangeTitleOption, showSaveAs, showHandInAssignment, showShareWithClass, options])

  function Option(props: MenuOptionButtonParams): ReactElement {
    return (
      <Button
        key={props.text}
        variant="contained"
        color="primary"
        onClick={props.onClick}
        className="more-options-button-base"
      >
        {props.icon}
        {props.text}
      </Button>
    )
  }

  function IconLabel(props: MenuOptionButtonParams): ReactElement {
    return (
      <>
        {props.icon}
        {props.text}
      </>
    )
  }

  function Options(): ReactElement {
    if (optionsToShow.length === 0) return <></>
    if (optionsToShow.length === 1) return <Option {...optionsToShow[0].buttonParams} />

    return (
      <ContextMenuButton linkButton className="more-options-button">
        {optionsToShow.map((option: MenuOption, key: number) =>
          option.buttonParams.nestedItems ? (
            <NestedMenuItem
              // biome-ignore lint/suspicious/noArrayIndexKey: TODO fix in a later PR
              key={key}
              label={<IconLabel {...option.buttonParams} />}
              parentMenuOpen={true}
              rightIcon={null}
            >
              {option.buttonParams.nestedItems}
            </NestedMenuItem>
          ) : (
            <IconMenuItem
              // biome-ignore lint/suspicious/noArrayIndexKey: TODO fix in a later PR
              key={key}
              className="ViewerContextMenuIconButton"
              icon={option.buttonParams.icon}
              label={option.buttonParams.text}
              onClick={option.buttonParams.onClick}
            />
          )
        )}
      </ContextMenuButton>
    )
  }

  return <Options />
}
